import { SyntheticEvent } from 'react';
import { SpacingToken } from '@optimus-web/common';
import { Stack } from '../Stack';
import { Typography } from '../typography';
import AccordionBody from './AccordionBody';
import { AccordionIconButton } from './AccordionIconButton';
import { DEFAULT_ACCORDION_SPACING, DEFAULT_PADDINGS } from './constants';
import {
    AccordionContainerElement,
    AccordionHeaderElement,
    AccordionHeaderLabelElement,
    AccordionHeaderLeftElement,
    AccordionHeaderRightElement,
} from './style';

export interface AccordionProps {
    expanded: boolean;
    onClick: () => void;
    body: React.ReactChild;
    startSlot?: React.ReactChild;
    /**
     * @deprecated middleSlot is deprecated.
     * Please use endSlot instead.
     */
    middleSlot?: React.ReactChild;
    endSlot?: React.ReactChild;
    title: React.ReactNode;
    description?: React.ReactNode;
    bodyPadding?: SpacingToken;
    headerPadding?: {
        top?: SpacingToken;
        bottom?: SpacingToken;
        left?: SpacingToken;
        right?: SpacingToken;
    };
    withBodyBorder?: boolean;
    withHeaderBorder?: boolean;
    arrowPosition?: 'left' | 'right';
}

export const Accordion = ({
    expanded,
    onClick,
    startSlot,
    middleSlot,
    endSlot,
    title,
    description,
    body,
    withBodyBorder = true,
    withHeaderBorder = true,
    bodyPadding = DEFAULT_ACCORDION_SPACING,
    headerPadding,
    arrowPosition = 'right',
    ...other
}: AccordionProps) => {
    const handleChildClick = (e: SyntheticEvent) => e.stopPropagation();

    return (
        <AccordionContainerElement {...other}>
            <AccordionHeaderElement
                onClick={onClick}
                data-test-accordion-header
                withHeaderBorder={withHeaderBorder}
                headerPadding={{ ...DEFAULT_PADDINGS, ...headerPadding }}
            >
                {arrowPosition === 'left' ? <AccordionIconButton expanded={expanded} /> : null}
                {startSlot ? (
                    <AccordionHeaderLeftElement onClick={handleChildClick}>{startSlot}</AccordionHeaderLeftElement>
                ) : null}
                <AccordionHeaderLabelElement>
                    <Stack vertical spacing="25">
                        <Typography textStyle="bodyLargeStrong" as="div">
                            {title}
                        </Typography>
                        {description ? (
                            <Typography textStyle="bodySmall" variant="secondary" as="div">
                                {description}
                            </Typography>
                        ) : null}
                    </Stack>
                    {middleSlot}
                </AccordionHeaderLabelElement>
                {endSlot ? (
                    <AccordionHeaderRightElement onClick={handleChildClick}>{endSlot}</AccordionHeaderRightElement>
                ) : null}
                {arrowPosition === 'right' ? <AccordionIconButton expanded={expanded} /> : null}
            </AccordionHeaderElement>
            <AccordionBody
                expanded={expanded}
                withBodyBorder={withBodyBorder}
                bodyPadding={bodyPadding}
                data-test-accordion-body
            >
                {body}
            </AccordionBody>
        </AccordionContainerElement>
    );
};
